import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, CircularProgress, Paper, Pill, Stack, Typography} from 'views/components';
import {translate} from 'views/module';

import {formatDateTime, translateOr} from 'views/scenes/loom_lead/detail/client_scenes/client_feed/format';

import backend from 'utils/backend';

const Message = (props) => {
  const {message} = props;
  const isInbound = message.direction === 'INBOUND';

  return (
    <Paper
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        borderLeft: '4px solid',
        borderLeftColor: isInbound ? '#2563EB' : '#DA7955',
      }}
    >
      <Stack spacing={1} sx={{p: 2}}>
        <Stack direction='row' alignItems='center' sx={{gap: '7px', flexWrap: 'wrap'}}>
          <Pill
            label={translateOr(`loomLead.LoomLeadDetail.Correspondance.Direction.${message.direction}`, message.direction)}
            variant={isInbound ? 'info' : 'neutral'}
          />
          <Pill
            label={translateOr(`loomLead.LoomLeadDetail.Correspondance.Channel.${message.channel}`, message.channel)}
            variant='neutral'
          />
          <Typography sx={{fontSize: '11px', color: '#A8A49E', ml: 'auto'}}>
            {formatDateTime(message.occurredAt)}
          </Typography>
        </Stack>
        <Stack spacing={0.25}>
          {
            message.sender &&
            <Typography sx={{fontSize: '12px', color: '#6B6760'}}>
              {translate('loomLead.LoomLeadDetail.Correspondance.From', {value: message.sender})}
            </Typography>
          }
          {
            message.recipients.length > 0 &&
            <Typography sx={{fontSize: '12px', color: '#6B6760'}}>
              {translate('loomLead.LoomLeadDetail.Correspondance.To', {value: message.recipients.join(', ')})}
            </Typography>
          }
        </Stack>
        {
          message.subject &&
          <Typography sx={{fontSize: '14px', fontWeight: 600, color: '#1A1916'}}>
            {message.subject}
          </Typography>
        }
        {
          message.text &&
          <Typography sx={{fontSize: '13px', color: '#1A1916', whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
            {message.text}
          </Typography>
        }
        {
          (message.textTruncated || message.subjectTruncated) &&
          <Typography sx={{fontSize: '11px', color: '#A8A49E'}}>
            {translate('loomLead.LoomLeadDetail.Correspondance.Truncated')}
          </Typography>
        }
      </Stack>
    </Paper>
  );
};
Message.propTypes = {
  message: PropTypes.object.isRequired,
};

// The caregiver's Loom messages, as the lead page's LoomCorrespondance shows a client's. Loom links
// a conversation to a caregiver only through a message routed to one of their workflows.
const LoomCaregiverCorrespondance = (props) => {
  const {caregiverId, refresh} = props;

  const [communications, setCommunications] = React.useState({messages: [], hasMore: false});
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    let isCancelled = false;

    const fetchCommunications = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await backend.loom.caregiver.listCommunications(caregiverId);
        if (!isCancelled) setCommunications(result);
      } catch (loadError) {
        console.error(loadError);
        if (isCancelled) return;
        setCommunications({messages: [], hasMore: false});
        setError(translate('loomLead.LoomLeadDetail.Correspondance.LoadError'));
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    fetchCommunications();

    return () => {
      isCancelled = true;
    };
  }, [caregiverId, refresh]);

  if (loading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 3}}>
        <CircularProgress />
      </Box>
    );
  }

  const {messages, hasMore} = communications;

  return (
    <Stack spacing={2} sx={{p: 2}}>
      {
        error &&
        <Alert severity='error'>{error}</Alert>
      }
      {
        !error && messages.length === 0 &&
        <Alert severity='info'>{translate('caregiver.LoomCaregiverDetail.Correspondance.Empty')}</Alert>
      }
      {
        hasMore &&
        <Alert severity='info'>
          {translate('loomLead.LoomLeadDetail.Correspondance.More', {count: messages.length})}
        </Alert>
      }
      {
        messages.map((message) => (
          <Message
            key={message.id}
            message={message}
          />
        ))
      }
    </Stack>
  );
};
LoomCaregiverCorrespondance.propTypes = {
  caregiverId: PropTypes.string.isRequired,
  refresh: PropTypes.number.isRequired,
};

export default React.memo(LoomCaregiverCorrespondance);
